/**
 * <variant-collapsible-content>
 *
 * Swaps the body of a collapsible text block when the shopper picks a different variant.
 *
 * Blocks like "What's in the Box" bind their content to a product-level metafield, which is the same for every
 * variant. Some variants genuinely differ - a Split King sheet set ships two fitted sheets where every other size
 * ships one - so this element takes a map of variant id to replacement HTML rendered by Liquid and applies it as
 * the selection changes, falling back to the Liquid-baked content for any variant absent from the map.
 *
 * Only variants that actually carry a value are in the map, so the common case adds nothing to the page.
 */
class VariantCollapsibleContent extends HTMLElement {
    constructor() {
        super();
        this._onVariantChangedListener = this._onVariantChanged.bind(this);
    }

    connectedCallback() {
        try {
            this._overrides = JSON.parse(this.dataset.overrides || "null");
        } catch (error) {
            this._overrides = null;
        }

        this._body = this.querySelector("[data-variant-collapsible-body]");
        this._defaultContent = this._body ? this._body.innerHTML : null;

        const form = document.forms[this.getAttribute("form")];
        form?.addEventListener("variant:change", this._onVariantChangedListener);

        // Liquid renders the product-level content, which is wrong when the page was deep-linked to a variant
        // that carries its own value (?variant=...). Settle the initial state from whatever the form already holds.
        this._render(form?.querySelector('[name="id"]')?.value);
    }

    disconnectedCallback() {
        document.forms[this.getAttribute("form")]?.removeEventListener("variant:change", this._onVariantChangedListener);
    }

    _onVariantChanged(event) {
        this._render(event.detail.variant?.id);
    }

    _render(variantId) {
        if (!this._body || !this._overrides || this._defaultContent === null) {
            return;
        }

        const override = variantId == null ? null : this._overrides[String(variantId)];
        const content = override == null ? this._defaultContent : override;

        // Guard against clobbering an open accordion with identical markup on every option change.
        if (this._body.innerHTML !== content) {
            this._body.innerHTML = content;
        }
    }
}

if (!window.customElements.get("variant-collapsible-content")) {
    window.customElements.define("variant-collapsible-content", VariantCollapsibleContent);
}

export { VariantCollapsibleContent };
